import { useEffect, useMemo, useState } from 'react';
import { getPrivateOrganizationId } from '@/src/listings/utils/utils';

// Note: The response has more fields, but we only need the activated field.
// Source: https://github.com/eventbrite/core/blob/master/django/src/www/eventbrite/ebapps/queue/client.py#L225
export interface WaitingRoomStatusResponse {
  activated: boolean;
}

interface UseWaitingRoomStatusProps {
  organizationId: string;
}

interface UseWaitingRoomStatusReturn {
  waitingRoomStatus: WaitingRoomStatusResponse | null;
  isLoading: boolean;
}

function isWaitingRoomStatusResponse(value: unknown): value is WaitingRoomStatusResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'activated' in value &&
    typeof (value as WaitingRoomStatusResponse).activated === 'boolean'
  );
}

/**
 * Fetches waiting room status for an organization.
 * Returns null on any failure: network error, non-ok response, invalid JSON, or invalid shape.
 * Low priority: callers should treat null as "feature unavailable" and never block the UI.
 * @internal Exported for testing.
 */
export const getWaitingRoomStatus = async (
  privateOrganizationId: string,
  signal?: AbortSignal,
): Promise<WaitingRoomStatusResponse | null> => {
  try {
    const response = await fetch(`/queue_rpc/status/${privateOrganizationId}`, { signal });
    if (!response.ok) {
      return null;
    }
    const body: unknown = await response.json();
    if (!isWaitingRoomStatusResponse(body)) {
      return null;
    }
    return body;
  } catch {
    return null;
  }
};

/**
 * Gets the waiting room status for an organization.
 * @param organizationId - The ID of the organization to get the waiting room status for.
 * @returns The waiting room status for the organization and a boolean indicating if the status call is loading.
 */
export const useWaitingRoomStatus = ({ organizationId }: UseWaitingRoomStatusProps): UseWaitingRoomStatusReturn => {
  const [waitingRoomStatus, setWaitingRoomStatus] = useState<WaitingRoomStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const privateOrganizationId = useMemo(() => getPrivateOrganizationId(organizationId), [organizationId]);

  useEffect(() => {
    if (!organizationId || !privateOrganizationId) {
      setWaitingRoomStatus(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const controller = new AbortController();

    getWaitingRoomStatus(privateOrganizationId, controller.signal)
      .then((status) => {
        if (status !== null) {
          setWaitingRoomStatus(status);
        }
      })
      .catch((error: Error) => {
        if (error.name !== 'AbortError') {
          setWaitingRoomStatus(null);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [organizationId, privateOrganizationId]);

  return { waitingRoomStatus, isLoading };
};
